import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  fetchEmployeeDetail,
  fetchRun,
  fetchRunEmployees,
  fetchRuns,
  type ListRunsParams,
  type PagingParams,
} from '../api/enrollment-overlap.api'
import { shouldRetryQuery } from '../utils/api-errors'
import { nextPollInterval } from '../utils/polling'

/** Every query of this feature lives under this key, so one call can reset it. */
export const ENROLLMENT_OVERLAP_QUERY_KEY = 'enrollment-overlap'

export const runsQueryKey = (params: ListRunsParams) =>
  [ENROLLMENT_OVERLAP_QUERY_KEY, 'runs', params] as const

export const runQueryKey = (runId: string | null) =>
  [ENROLLMENT_OVERLAP_QUERY_KEY, 'run', runId] as const

export function useRunsQuery(params: ListRunsParams) {
  return useQuery({
    queryKey: runsQueryKey(params),
    queryFn: () => fetchRuns(params),
    placeholderData: keepPreviousData,
    retry: shouldRetryQuery,
  })
}

/** The newest SUCCEEDED run, independent of the list's page and filter. */
export function useLastSuccessfulRunQuery() {
  const params: ListRunsParams = {
    page: 1,
    pageSize: 1,
    status: 'SUCCEEDED',
  }
  return useQuery({
    queryKey: runsQueryKey(params),
    queryFn: () => fetchRuns(params),
    select: (list) => list.items[0] ?? null,
    retry: shouldRetryQuery,
  })
}

/**
 * Fetches one run. While `poll` is set it re-fetches every ~5s until the run
 * leaves RUNNING; React Query stops the timer when the component unmounts.
 */
export function useRunQuery(
  runId: string | null,
  { poll = false }: { poll?: boolean } = {}
) {
  return useQuery({
    queryKey: runQueryKey(runId),
    queryFn: () => fetchRun(runId as string),
    enabled: runId !== null,
    retry: shouldRetryQuery,
    refetchInterval: poll
      ? (query) =>
          nextPollInterval(
            query.state.data?.status,
            query.state.error,
            query.state.data?.startedAt
          )
      : false,
  })
}

export function useRunEmployeesQuery(runId: string, paging: PagingParams) {
  return useQuery({
    queryKey: [ENROLLMENT_OVERLAP_QUERY_KEY, 'employees', runId, paging],
    queryFn: () => fetchRunEmployees(runId, paging),
    placeholderData: keepPreviousData,
    retry: shouldRetryQuery,
    // Items are empty until the run SUCCEEDS, so keep checking while it runs.
    refetchInterval: (query) =>
      query.state.data
        ? nextPollInterval(
            query.state.data.run.status,
            query.state.error,
            query.state.data.run.startedAt
          )
        : false,
  })
}

export function useEmployeeDetailQuery(runId: string, employeeKey: string) {
  return useQuery({
    queryKey: [ENROLLMENT_OVERLAP_QUERY_KEY, 'employee', runId, employeeKey],
    queryFn: () => fetchEmployeeDetail(runId, employeeKey),
    retry: shouldRetryQuery,
  })
}
